// Necessary. Built-in type error

/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import dayjs from 'dayjs';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Options } from 'rrule';
import { RRule, Frequency } from 'rrule';

import { DAYS_IN_WEEK, MONTHS_DATES } from '@constants/date';

import { locale, suffixes, pluralRules } from '@intl/index';

import type { TimeSlot } from '@models/routine';

import { capitalize, formatTime } from '@utils/format';
import { formatDate } from '@utils/format';
import {
  freqToText,
  getDaysOfWeekDates,
  weekNumberToText,
} from '@utils/routines';

const messages = defineMessages({
  from: {
    id: 'rRuleNlp.from',
    defaultMessage: 'from',
  },
  to: {
    id: 'rRuleNlp.to',
    defaultMessage: 'to',
  },
  at: {
    id: 'rRuleNlp.from',
    defaultMessage: 'From',
  },
  every: {
    id: 'rRuleNlp.every',
    defaultMessage: 'Every',
  },
  in: {
    id: 'rRuleNlp.in',
    defaultMessage: 'in',
  },
  and: {
    id: 'rRuleNlp.and',
    defaultMessage: 'and',
  },
  on: {
    id: 'rRuleNlp.on',
    defaultMessage: 'on',
  },
  the: {
    id: 'rRuleNlp.the',
    defaultMessage: 'the',
  },
  for: {
    id: 'rRuleNlp.for',
    defaultMessage: 'for',
  },
  until: {
    id: 'rRuleNlp.until',
    defaultMessage: 'until',
  },
  occurrences: {
    id: 'rRuleNlp.occurrences',
    defaultMessage: 'occurrences',
  },
});

function RRuleNlp({
  rrule,
  timeSlots,
}: {
  /**
   * ICal formatted recurrence rule
   */
  rrule: string;
  /**
   * Time slots that the recurrence rule repeats
   */
  timeSlots: TimeSlot[];
}) {
  const { formatMessage, formatList } = useIntl();
  if (rrule === '') {
    return <div />;
  }
  const { options } = RRule.fromString(rrule);

  /**
   * Get the ending string for a recurrence rule
   * @param options RRule options
   * @returns A string representing when a recurrence ends
   */
  const getUntil = (options: Options) => {
    const { count, until } = options;
    if (until) {
      return `${formatMessage(messages.until)} ${formatDate({ date: until })}`;
    } else if (count) {
      return `${formatMessage(messages.for)} ${count} ${formatMessage(
        messages.occurrences
      )}`;
    }
    return '';
  };

  // 1 = Monday, rrule first day of week is always Monday but Dx Scheduler is configurable and set to Sunday
  const daysOfWeek = getDaysOfWeekDates(1);

  return (
    <div>
      {`${formatMessage(messages.every)} ${
        options.interval > 1 ? options.interval : ''
      } ${
        options.freq === Frequency.MONTHLY && options.bymonth.length
          ? ''
          : freqToText(
              options.freq,
              formatMessage,
              options.interval > 1
            ).toLowerCase()
      }
       ${
         options.bymonth?.length
           ? `${formatMessage(messages.in)} ${formatList(
               options.bymonth.map((month) =>
                 dayjs(MONTHS_DATES[month - 1]).format('MMMM')
               ),
               { type: 'conjunction' }
             )}`
           : ''
       } 
      ${
        options.bynweekday?.length
          ? `${formatMessage(messages.on)} ${formatMessage(
              messages.the
            )} ${formatList(
              options.bynweekday.map(([weekday, weekNo]) => {
                return `${weekNumberToText(weekNo, formatMessage)} ${dayjs(
                  daysOfWeek[weekday + 1 < DAYS_IN_WEEK ? weekday + 1 : 0]
                ).format('dddd')}`;
              }),
              { type: 'conjunction' }
            )}`
          : ''
      } 
      ${
        options.bymonthday?.length
          ? `${formatMessage(messages.on)} ${formatMessage(
              messages.the
            )} ${formatList(
              options.bymonthday.map(
                (monthday) =>
                  `${monthday}${suffixes[locale][pluralRules.select(monthday)]}`
              ),
              { type: 'conjunction' }
            )}`
          : ''
      } 
      ${
        options.byweekday?.length
          ? `${formatMessage(messages.on)} ${formatList(
              options.byweekday.map((weekday) =>
                dayjs(daysOfWeek[weekday]).format('dddd')
              ),
              { type: 'conjunction' }
            )}`
          : ''
      } 
      ${
        timeSlots?.length
          ? formatList(
              timeSlots.map(
                (slot) =>
                  `${capitalize(
                    formatMessage(messages.from)
                  ).toLowerCase()} ${formatTime(
                    slot.startTime
                  )} ${formatMessage(messages.to).toLowerCase()} ${formatTime(
                    slot.endTime
                  )}`
              ),
              { type: 'conjunction' }
            )
          : ''
      }
      ${getUntil(options)}
      `}
    </div>
  );
}

export default RRuleNlp;
